'use client';

import { entityUrl } from '@/lib/entity-url';
import React, { useEffect, useId, useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useTagData } from '../../hooks/useTagData';
import { useFirestoreCrypto } from '../../hooks/useFirestoreCrypto';
import { useTagPrefetch } from '@/contexts/TagPrefetchContext';
import { useMentionDedupe } from '@/components/ui/MentionDedupe';

interface TagMentionProps {
    slug: string;
}

export const TagMention: React.FC<TagMentionProps> = ({ slug }) => {
    const [isMounted, setIsMounted] = useState(false);
    const { getTag, prefetchTags, isTagCached } = useTagData();
    const { data: cryptoData } = useFirestoreCrypto();
    const { prefetchedTags } = useTagPrefetch();

    // Track when component mounts on client
    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Try to get tag from prefetched data first, then fall back to client fetch
    const prefetchedTag = prefetchedTags[slug];
    const clientTag = getTag(slug);
    const tag = prefetchedTag || clientTag;

    // De-dupe within a single document: only the FIRST mention of an entity
    // shows the image-chip; repeats render as a plain entity link. Key on the
    // resolved entity slug so the same entity referenced by both slug and
    // twitter handle collapses to one. `ready` gates the claim until the tag has
    // resolved (pre-resolution shows no image anyway), and the stable instance id
    // keeps ownership idempotent across this mention's async re-renders.
    // (No provider in scope → always first → chip renders as before.)
    const mentionInstanceId = useId();
    const dedupeReady = isMounted && !!tag;
    const { isFirstMention } = useMentionDedupe(tag?.slug || slug, mentionInstanceId, dedupeReady);

    useEffect(() => {
        // Only fetch if not prefetched and mounted
        if (isMounted && !tag && !prefetchedTag) {
            prefetchTags([slug]);
        }
    }, [slug, tag, prefetchedTag, prefetchTags, isMounted]);

    // Memoize price data calculation
    const priceData = useMemo(() => {
        if (!isMounted || !tag?.linkedAsset || !cryptoData) return null;

        if (tag.linkedAsset.type === 'token') {
            const symbol = tag.linkedAsset.assetId.toUpperCase();

            // Check Bitcoin
            if (symbol === 'BTC' && cryptoData.bitcoin) {
                return {
                    price: cryptoData.bitcoin.currentPrice,
                    change: cryptoData.bitcoin.priceChange24h
                };
            }

            // Check Ethereum
            if (symbol === 'ETH' && cryptoData.ethereum) {
                return {
                    price: cryptoData.ethereum.currentPrice,
                    change: cryptoData.ethereum.priceChange24h
                };
            }

            // Check Abstract Tokens
            const token = cryptoData.abstract_tokens.find(t => t.symbol.toUpperCase() === symbol);
            if (token) {
                return {
                    price: token.currentPrice,
                    change: token.priceChange24h
                };
            }
        }
        return null;
    }, [tag, cryptoData, isMounted]);

    // Memoize image URL calculation
    const imageUrl = useMemo(() => {
        if (!isMounted || !tag) return null;

        // 1. Try tag images first (prefer primary image, fall back to first)
        if (tag.images && tag.images.length > 0) {
            const primaryImage = tag.images.find(img => img.isPrimary);
            return primaryImage?.url || tag.images[0].url;
        }

        // 2. Try linked asset image from crypto data
        if (tag.linkedAsset?.type === 'token' && cryptoData) {
            const symbol = tag.linkedAsset.assetId.toUpperCase();

            // Check Abstract Tokens
            const token = cryptoData.abstract_tokens.find(t => t.symbol.toUpperCase() === symbol);
            if (token) {
                return token.customImage || token.imageUrl || null;
            }

            // Fallback for major coins if needed (optional)
            if (symbol === 'BTC') return 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png';
            if (symbol === 'ETH') return 'https://assets.coingecko.com/coins/images/279/large/ethereum.png';
        }

        return null;
    }, [tag, cryptoData, isMounted]);

    // If tag is not found but we have confirmed it doesn't exist (cached as null),
    // render as plain text instead of a faulty handle/skeleton
    if (isMounted && !tag && isTagCached(slug)) {
        return <span>@{slug}</span>;
    }

    // Always render skeleton until mounted and tag is loaded
    // This ensures server and client render the same thing initially
    if (!isMounted || !tag) {
        // Render subtle skeleton - just underlined text, no circle
        return (
            <span className="text-gray-400 animate-pulse border-b border-gray-300 dark:border-neutral-600">
                {slug}
            </span>
        );
    }

    return (
        <Link
            href={entityUrl(tag.slug, { trailingSlash: false })}
            className="inline-flex items-baseline gap-1 text-scout-gold hover:text-scout-gold/90 transition-colors no-underline group align-baseline"
            onClick={(e) => e.stopPropagation()}
        >
            {imageUrl && isFirstMention && (
                <span className="w-4 h-4 rounded-full overflow-hidden ring-1 ring-scout-gold/30 group-hover:ring-scout-gold/60 transition-all translate-y-0.5 flex-shrink-0 relative inline-block">
                    <Image
                        src={imageUrl}
                        alt={tag.name}
                        fill
                        sizes="16px"
                        className="object-cover"
                        loading="lazy"
                        onError={(e) => {
                            const container = e.currentTarget.parentElement;
                            if (container) {
                                container.style.display = 'none';
                            }
                        }}
                    />
                </span>
            )}
            <span className="border-b border-scout-gold/40 group-hover:border-scout-gold transition-colors">{tag.name}</span>

            {priceData && (
                <span className={`text-xs font-medium ml-0.5 ${priceData.change >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                    {priceData.change >= 0 ? '↑' : '↓'}{Math.abs(priceData.change).toFixed(1)}%
                </span>
            )}
        </Link>
    );
};

export default TagMention;
